import React from 'react'

import { AvatarStack, Icon, Tag, Text, Touchable } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useEmployeeListForSoftwareDataTable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { InlineEmployee } from 'modules/employees/components/InlineEmployee'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useTerminateEmployees } from 'modules/software_management/hooks/useTerminateEmployees'

const TerminatedEmployeesPage = () => {
  useEffect(() => {
    track(trackingEvents.softwareManagement.goToTerminatedEmployeeList)
  }, [])

  const { debouncedSearch, search, onSearch } = useDebouncedSearch()
  const { navigation } = useNavigation()
  const { delete: terminateEmployees } = useTerminateEmployees()

  const source = useEmployeeListForSoftwareDataTable(
    [
      {
        identifier: 'employee_fullName',
        title: i18n.t('software_management.software_setups.tabs.employees.table.name'),
        kind: ProjectionKind.Custom,
        render: (employee) => {
          return <InlineEmployee employee={employee} variant="medium" />
        },
        template: 'employee',
      },
      {
        identifier: 'software_logos',
        title: 'Software installed',
        kind: ProjectionKind.Custom,
        render: ({ employeeSoftwareMemberships }) => {
          const avatars = employeeSoftwareMemberships.map((esm) => ({
            url: esm.software ? esm.software.logoUrl : esm.customSoftware!.logoUrl,
            label: esm.software ? esm.software.name : esm.customSoftware!.name,
          }))

          return (
            <AvatarStack
              size="s40"
              overlap="s12"
              avatars={avatars}
              leftover={0}
              textSize={'200'}
              textColor={'grey000White'}
              backgroundColor={'champagne1000'}
            />
          )
        },
        template: 'email',
      },
      {
        identifier: 'employee_team',
        title: i18n.t('software_management.software_setups.tabs.employees.table.team'),
        kind: ProjectionKind.Custom,
        render: ({ memberships }) => {
          return <Tag inline>{memberships[0]?.team?.name}</Tag>
        },
        template: 'team name',
      },
      {
        identifier: 'employee_job',
        title: i18n.t('software_management.software_setups.tabs.employees.table.job'),
        kind: ProjectionKind.Custom,
        render: ({ referenceContract }) => {
          return <Tag inline>{referenceContract?.jobTitle}</Tag>
        },
        template: 'job title',
      },
      {
        identifier: 'startsOn',
        title: i18n.t('software_management.software_setups.tabs.employees.table.member_since'),
        kind: ProjectionKind.Custom,
        render: ({ referenceContract }) => {
          return (
            <Text.Normal size="200">
              {referenceContract?.startsOn
                ? dateBackendDateTimeToUI(referenceContract?.startsOn)
                : ''}
            </Text.Normal>
          )
        },
        template: 'member since',
      },
      {
        identifier: 'location',
        title: i18n.t('software_management.software_setups.tabs.employees.table.location'),
        kind: ProjectionKind.Custom,
        render: ({ locations }) => {
          return <Tag inline>{locations[0]?.name}</Tag>
        },
        template: 'location name',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: (employee) => {
          return (
            <Touchable onClick={() => terminateEmployeesWithFeedback([employee.id])}>
              <Icon.Medium icon="delete" color="radicalRed1000" />
            </Touchable>
          )
        },
      },
    ],
    {
      variables: {
        search: debouncedSearch,
        onlyTerminated: true,
        withSoftware: true,
      },
    }
  )
  const terminateEmployeesWithFeedback = async (employeeIds: string[]) => {
    const handled = handleResult(await terminateEmployees({ employeeIds }), () => undefined)

    if (handled.success) {
      track(trackingEvents.softwareManagement.offboardTerminatedEmployees, {
        employee_ids: employeeIds,
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('software_management.terminated_employees.offboard.success'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('software_management.terminated_employees.offboard.error'),
      })
    }
  }

  return (
    <ResponsivePage.Body variant="wide">
      <DataTable
        source={source}
        disableDefaultActions
        sort={{ path: 'id', direction: 'desc' }}
        search={{
          placeholder: i18n.t('common.search'),
          value: search,
          onChange: onSearch,
        }}
        rowTo={(employee) =>
          navigation.software.applications.terminatedEmployees.employeeId.fullPath({
            employeeId: employee.id,
          })
        }
      />
    </ResponsivePage.Body>
  )
}

export default TerminatedEmployeesPage
